/**
 * Bubble Cloud Component
 *
 * Animated 3D sphere of colored dots rendered on canvas.
 * Dots are clustered by status category with a scanning highlight effect.
 * A donut-style ring around the sphere animates in on first view.
 * Reads category counts and colors from data attributes.
 *
 * @package oska
 */

// -- Math utilities --

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function easeWithPause(t) {
  const pauseDuration = 0.1;
  const activeDuration = 1 - 2 * pauseDuration;

  if (t < pauseDuration) return 0;
  if (t > 1 - pauseDuration) return 1;

  const activeT = (t - pauseDuration) / activeDuration;
  return easeInOutCubic(activeT);
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function hexToRgb(hex) {
  if (hex.startsWith("#") && hex.length >= 7) {
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  }
  return [255, 255, 255];
}

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gaussian(rng) {
  let u = 0;
  let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function randomPointInSphere(rng, radius) {
  const u = rng();
  const v = rng();
  const w = rng();

  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  const rr = radius * Math.cbrt(w);

  const sinPhi = Math.sin(phi);
  return {
    x: rr * sinPhi * Math.cos(theta),
    y: rr * sinPhi * Math.sin(theta),
    z: rr * Math.cos(phi),
  };
}

function randomPointAroundCenter(rng, center, sigma, radius) {
  let x = center.x + gaussian(rng) * sigma;
  let y = center.y + gaussian(rng) * sigma;
  let z = center.z + gaussian(rng) * sigma;

  const d = Math.sqrt(x * x + y * y + z * z);
  if (d > radius) {
    const k = (radius / d) * (0.98 + rng() * 0.015);
    x *= k;
    y *= k;
    z *= k;
  }

  return { x, y, z };
}

function rotateY(p, a) {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x * c - p.z * s, y: p.y, z: p.x * s + p.z * c };
}

function rotateX(p, a) {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function normalize3(v) {
  const d = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / d, y: v.y / d, z: v.z / d };
}

function dot3(a, b) {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

// -- Dot generation --

/**
 * Distribute N cluster centers evenly inside the sphere using a Fibonacci spiral.
 * Returns an array of {x, y, z} positions.
 */
function makeClusterCenters(count, radius) {
  const r = radius * 0.55;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const centers = [];

  for (let i = 0; i < count; i++) {
    const y = count === 1 ? 0 : 1 - (i / (count - 1)) * 2;
    const rAtY = Math.sqrt(1 - y * y);
    const theta = i * goldenAngle;

    centers.push({
      x: r * rAtY * Math.cos(theta),
      y: r * y,
      z: r * rAtY * Math.sin(theta),
    });
  }

  return centers;
}

function generateDots(categories, radius, seed) {
  const rng = mulberry32(seed);
  const centers = makeClusterCenters(categories.length, radius);

  // Keep most dots near their category center so the sphere still reads as
  // grouped, but leave some points fully mixed to avoid hard cluster borders.
  const CLUSTER_SHARE = 0.8;
  const SIGMA = radius * 0.15;

  const dots = [];

  categories.forEach((cat, idx) => {
    const clusterCount = Math.round(cat.count * CLUSTER_SHARE);
    const backgroundCount = cat.count - clusterCount;
    const center = centers[idx];

    // Clustered dots make each status legible as a group inside the sphere.
    for (let i = 0; i < clusterCount; i++) {
      const p = randomPointAroundCenter(rng, center, SIGMA, radius);
      dots.push({ x: p.x, y: p.y, z: p.z, color: cat.color, jitter: rng() });
    }

    // The remaining dots are fully mixed through the sphere so the cloud keeps
    // a natural blended look instead of forming hard category islands.
    for (let i = 0; i < backgroundCount; i++) {
      const p = randomPointInSphere(rng, radius);
      dots.push({ x: p.x, y: p.y, z: p.z, color: cat.color, jitter: rng() });
    }
  });

  // Shuffle for natural layering
  for (let i = dots.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = dots[i];
    dots[i] = dots[j];
    dots[j] = tmp;
  }

  return dots;
}

// -- Ring segment computation --

function buildRingSegments(categories) {
  const total = categories.reduce((s, c) => s + c.count, 0);
  if (total === 0) return [];

  const START = -Math.PI / 2;
  let angle = START;

  return categories
    .filter((c) => c.count > 0)
    .map((cat) => {
      const sweep = (cat.count / total) * Math.PI * 2;
      const seg = {
        startAngle: angle,
        endAngle: angle + sweep,
        color: cat.color,
        rgb: hexToRgb(cat.color),
      };
      angle += sweep;
      return seg;
    });
}

// -- BubbleCloud class --

class BubbleCloud {
  constructor(element) {
    this.container = element;

    let parsed = [];
    try {
      parsed = JSON.parse(element.dataset.categories || "[]");
    } catch (_) {
      parsed = [];
    }

    this.categories = parsed.map((cat) => ({
      slug: cat.slug,
      count: parseInt(cat.count, 10) || 0,
      color: cat.color || "#0171CF",
    }));

    this.seed = parseInt(element.dataset.seed, 10) || 42;
    this.sphereBg = element.dataset.sphereBg || "#ecf2f8";

    this.raf = 0;
    this.lastTs = 0;
    this.time = 0;
    this.running = false;

    this.canvas = null;
    this.ctx = null;
    this.dots = [];
    this.rgbCache = new Map();

    // Ring animation state
    this.ringSegments = [];
    this.ringAnimStarted = false;
    this.ringAnimStartTime = 0;
    this.ringAnimDuration = 800;
    this.ringAnimProgress = 0;

    // Dots animation state (starts at 50% of ring animation)
    this.dotsAnimStartTime = 0;
    this.dotsAnimDuration = 800;
    this.dotsAnimProgress = 0;

    // Highlight state - when set, only this color is fully visible
    this.highlightColor = null;
    this.highlightOpacity = 0; // Current animated opacity (0 = no highlight, 1 = full highlight)
    this.targetHighlightOpacity = 0;

    this.init();
  }

  init() {
    this.createCanvas();
    this.rebuild();
    this.setupObserver();
  }

  createCanvas() {
    this.canvas = document.createElement("canvas");
    this.canvas.className = "bubble-cloud__canvas";
    this.container.appendChild(this.canvas);
  }

  measure() {
    const rect = this.container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssSize = Math.min(rect.width, 620);

    this.cssSize = cssSize;
    this.dpr = dpr;
    this.canvasPixels = Math.round(cssSize * dpr);
    this.diameter = cssSize * 0.82;
    this.R = this.diameter / 2;
    this.sizeScale = this.diameter / 460;
    this.scanWidth = 44 * this.sizeScale;

    this.canvas.width = this.canvasPixels;
    this.canvas.height = this.canvasPixels;
    this.canvas.style.width = cssSize + "px";
    this.canvas.style.height = cssSize + "px";
  }

  rebuild() {
    this.measure();
    this.dots = generateDots(this.categories, this.R, this.seed);
    this.ringSegments = buildRingSegments(this.categories);

    this.rgbCache.clear();
    for (const d of this.dots) {
      if (!this.rgbCache.has(d.color)) {
        this.rgbCache.set(d.color, hexToRgb(d.color));
      }
    }
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.start();
          } else {
            this.stop();
          }
        }
      },
      { threshold: 0.15 },
    );
    this.observer.observe(this.container);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastTs = 0;

    if (!this.ringAnimStarted) {
      this.ringAnimStarted = true;
      this.ringAnimStartTime = 0;
    }

    this.raf = requestAnimationFrame((ts) => this.frame(ts));
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  frame(ts) {
    if (!this.running) return;

    if (!this.lastTs) this.lastTs = ts;
    const dt = ts - this.lastTs;
    this.lastTs = ts;

    // Ring intro animation
    if (this.ringAnimProgress < 1) {
      if (!this.ringAnimStartTime) this.ringAnimStartTime = ts;
      const elapsed = ts - this.ringAnimStartTime;
      this.ringAnimProgress = Math.min(elapsed / this.ringAnimDuration, 1);
    }

    // The dot field intentionally lags behind the ring so the intro reads as
    // outline first, then volume, instead of both layers competing at once.
    if (this.ringAnimProgress >= 0.5 && this.dotsAnimProgress < 1) {
      if (!this.dotsAnimStartTime) this.dotsAnimStartTime = ts;
      const elapsed = ts - this.dotsAnimStartTime;
      this.dotsAnimProgress = Math.min(elapsed / this.dotsAnimDuration, 1);
    }

    // Sphere rotation only starts once dots begin appearing
    if (this.dotsAnimProgress > 0) {
      this.time += dt * 0.001 * 0.92;
    }

    // Animate highlight opacity
    const highlightSpeed = 0.008; // Speed of opacity transition
    if (this.highlightOpacity !== this.targetHighlightOpacity) {
      const diff = this.targetHighlightOpacity - this.highlightOpacity;
      this.highlightOpacity += diff * Math.min(highlightSpeed * dt, 1);
      // Snap when close enough
      if (Math.abs(diff) < 0.01) {
        this.highlightOpacity = this.targetHighlightOpacity;
      }
    }

    this.render();
    this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  render() {
    const ctx = this.ctx || (this.ctx = this.canvas.getContext("2d"));
    if (!ctx) return;

    const size = this.canvasPixels;
    const dpr = this.dpr;
    const R = this.R * dpr;
    const cx = size / 2;
    const cy = size / 2;
    const sizeScale = this.sizeScale * dpr;
    const scanWidth = this.scanWidth * dpr;
    const time = this.time;

    ctx.clearRect(0, 0, size, size);

    // Sphere background
    ctx.fillStyle = this.sphereBg;
    ctx.beginPath();
    ctx.arc(cx, cy, R + 0.5 * dpr, 0, Math.PI * 2);
    ctx.fill();

    const rotYAngle = time * 0.55;
    const rotXAngle = Math.sin(time * 0.35) * 0.42;

    const scanPhase = (Math.sin(time * 1.36) + 1) / 2;
    const eased = easeWithPause(scanPhase);
    const scanOffset = lerp(-R, R, eased);

    // The scan plane slowly changes orientation over time so the highlight does
    // not always travel on the same vertical slice through the sphere.
    const n = normalize3({
      x: Math.cos(time * 0.22) * 0.35,
      y: 1,
      z: Math.sin(time * 0.22) * 0.35,
    });

    const dotsProgress = this.dotsAnimProgress;

    if (dotsProgress > 0) {
      const projected = [];
      // Stagger window: each dot fades in over 30% of the total duration,
      // offset by its jitter value across the remaining 70%
      const FADE_SPAN = 0.3;

      for (const dot of this.dots) {
        const dotStart = dot.jitter * (1 - FADE_SPAN);
        const dotLocal = Math.min(
          Math.max((dotsProgress - dotStart) / FADE_SPAN, 0),
          1,
        );
        if (dotLocal <= 0) continue;

        const dotT = easeOutCubic(dotLocal);

        let p = rotateY(dot, rotYAngle);
        p = rotateX(p, rotXAngle);

        const px = cx + p.x * dpr;
        const py = cy + p.y * dpr;
        const pz = p.z * dpr;

        // The pseudo-perspective scale pushes front dots slightly larger than
        // back dots without switching to a full camera/projection model.
        const scale = (pz + R * 1.55) / (R * 2.65);

        // The moving scan plane boosts points that sit close to the plane,
        // which creates the sweeping highlight without changing dot positions.
        const distToPlane = Math.abs(
          dot3(n, { x: p.x * dpr, y: p.y * dpr, z: pz }) - scanOffset,
        );
        const influence =
          distToPlane < scanWidth
            ? Math.cos((distToPlane / scanWidth) * (Math.PI / 2))
            : 0;

        const baseSize = Math.max(0, (0.9 + scale * 2.2) * sizeScale);
        const dotSize =
          (baseSize + influence * (2.8 + dot.jitter * 0.6) * sizeScale) * dotT;

        const dx = px - cx;
        const dy = py - cy;
        if (dx * dx + dy * dy > (R + 0.5 * dpr) * (R + 0.5 * dpr)) continue;

        projected.push({
          px,
          py,
          z: pz,
          size: dotSize,
          color: dot.color,
          opacity: dotT,
        });
      }

      // Painter's-order draw keeps nearer dots on top after the 3D projection.
      projected.sort((a, b) => a.z - b.z);

      for (const item of projected) {
        const rgb = this.rgbCache.get(item.color) || [255, 255, 255];
        // Dim non-highlighted colors when highlight is active (animated)
        const isHighlighted =
          this.highlightColor &&
          item.color.toLowerCase() === this.highlightColor;
        const dimAmount = isHighlighted ? 0 : (1 - 0.1) * this.highlightOpacity; // lerp from 1 to 0.1
        const finalOpacity = item.opacity * (1 - dimAmount);
        ctx.fillStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${finalOpacity})`;
        ctx.beginPath();
        ctx.arc(item.px, item.py, item.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    this.renderRing(ctx, cx, cy, R, dpr);
  }

  renderRing(ctx, cx, cy, R, dpr) {
    if (this.ringSegments.length === 0) return;

    const strokeWidth = R * 0.041;
    const ringRadius = R + strokeWidth * 0.05;
    const capRadius = strokeWidth / 2;
    const animT = easeOutCubic(this.ringAnimProgress);
    const startRef = -Math.PI / 2;

    // Overlap: each arc extends slightly past its end so it tucks under the next start cap
    const overlap = (capRadius * 0.5) / ringRadius;

    // Animate every segment from the shared top reference point so the ring
    // grows as one sweep instead of each segment expanding independently.
    const animated = this.ringSegments.map((seg) => {
      const curStart = startRef + (seg.startAngle - startRef) * animT;
      const curEnd = startRef + (seg.endAngle - startRef) * animT;
      return { curStart, curEnd, color: seg.color, rgb: seg.rgb };
    });

    // Pass 1 paints the arc bodies.
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = "butt";
    for (const a of animated) {
      if (a.curEnd - a.curStart < 0.001) continue;
      const isHighlighted =
        this.highlightColor && a.color.toLowerCase() === this.highlightColor;
      const dimAmount = isHighlighted ? 0 : (1 - 0.1) * this.highlightOpacity;
      const opacity = 1 - dimAmount;
      ctx.strokeStyle = `rgba(${a.rgb[0]}, ${a.rgb[1]}, ${a.rgb[2]}, ${opacity})`;
      ctx.beginPath();
      ctx.arc(cx, cy, ringRadius, a.curStart, a.curEnd);
      ctx.stroke();
    }

    // Pass 2 adds round trailing caps so adjacent segments look continuous even
    // though the main arcs themselves are drawn with butt line caps.
    for (const a of animated) {
      if (a.curEnd - a.curStart < 0.001) continue;
      const isHighlighted =
        this.highlightColor && a.color.toLowerCase() === this.highlightColor;
      const dimAmount = isHighlighted ? 0 : (1 - 0.1) * this.highlightOpacity;
      const opacity = 1 - dimAmount;
      ctx.fillStyle = `rgba(${a.rgb[0]}, ${a.rgb[1]}, ${a.rgb[2]}, ${opacity})`;
      ctx.beginPath();
      ctx.arc(
        cx + ringRadius * Math.cos(a.curEnd),
        cy + ringRadius * Math.sin(a.curEnd),
        capRadius,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }

    // Pass 3 redraws only the highlighted start cap so it can sit above the
    // neighboring segment that normally hides start joins.
    if (this.highlightOpacity > 0) {
      for (const a of animated) {
        if (a.curEnd - a.curStart < 0.001) continue;
        const isHighlighted =
          this.highlightColor && a.color.toLowerCase() === this.highlightColor;
        if (isHighlighted) {
          ctx.fillStyle = `rgba(${a.rgb[0]}, ${a.rgb[1]}, ${a.rgb[2]}, ${this.highlightOpacity})`;
          ctx.beginPath();
          ctx.arc(
            cx + ringRadius * Math.cos(a.curStart),
            cy + ringRadius * Math.sin(a.curStart),
            capRadius,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }
    }
  }

  update(categories) {
    if (Array.isArray(categories)) {
      this.categories = categories.map((cat) => ({
        slug: cat.slug,
        count: parseInt(cat.count, 10) || 0,
        color: cat.color || "#0171CF",
      }));
    }
    this.rebuild();
  }

  setHighlight(color) {
    this.highlightColor = color ? color.toLowerCase() : null;
    this.targetHighlightOpacity = color ? 1 : 0;
  }

  destroy() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

/**
 * Initialize all bubble cloud instances
 */
export default function initBubbleCloud() {
  const containers = document.querySelectorAll("[data-bubble-cloud]");
  const instances = new Map();

  containers.forEach((container) => {
    if (container.querySelector(".bubble-cloud__canvas")) return;

    const instance = new BubbleCloud(container);
    instances.set(container, instance);
    container._bubbleCloud = instance;
  });

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      instances.forEach((instance) => instance.rebuild());
    }, 200);
  });

  return instances;
}
